import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Transaction } from '../../services/dashboardService';

export interface NotificationModalProps {
  show: boolean;
  onHide: () => void;
  transactions: Transaction[];
  loading?: boolean;
}

type TabKey = 'all' | 'payment' | 'follow_up' | 'delivery';

type NotificationCategory = 'payments' | 'follow_up' | 'delivery';

interface NotificationItem {
  id: string;
  category: NotificationCategory;
  title: string;
  description: string;
  timestamp: string;
  href: string;
  transactionId: number;
}

const formatNotificationDate = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatAmount = (tx: Transaction): string => {
  if (tx.fiatAmount) return ` (${tx.fiatAmount} ${tx.fiatCurrency})`;
  if (tx.amount) return ` (${tx.amount} ${tx.currency})`;
  return '';
};

export const transactionsToNotifications = (transactions: Transaction[]): NotificationItem[] =>
  transactions.flatMap((tx) => {
    const amountStr = formatAmount(tx);
    const timestamp = formatNotificationDate(tx.timestamp);
    const items: NotificationItem[] = [
      {
        id: `payment-${tx.id}`,
        category: 'payments',
        title: `Transaction #${tx.id} — ${tx.status}`,
        description: `Payment ${tx.status.toLowerCase()}${amountStr}.`,
        timestamp,
        href: '/transactions',
        transactionId: tx.id,
      },
    ];
    if (tx.status === 'Pending' || tx.status === 'Failed') {
      items.push({
        id: `follow-${tx.id}`,
        category: 'follow_up',
        title: `Transaction #${tx.id} needs attention`,
        description: `This payment is ${tx.status.toLowerCase()}${amountStr}.`,
        timestamp,
        href: '/transactions',
        transactionId: tx.id,
      });
    }
    return items;
  });

function computeTabCounts(items: NotificationItem[]): Record<TabKey, number> {
  return {
    all: items.length,
    payment: items.filter((n) => n.category === 'payments').length,
    follow_up: items.filter((n) => n.category === 'follow_up').length,
    delivery: items.filter((n) => n.category === 'delivery').length,
  };
}

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  payments: 'Payments',
  follow_up: 'Follow up',
  delivery: 'Delivery',
};

function matchesTab(item: NotificationItem, tab: TabKey): boolean {
  if (tab === 'all') return true;
  if (tab === 'payment') return item.category === 'payments';
  if (tab === 'follow_up') return item.category === 'follow_up';
  return item.category === 'delivery';
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  show,
  onHide,
  transactions,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('all');

  const notifications = useMemo(() => transactionsToNotifications(transactions), [transactions]);

  const filtered = useMemo(
    () => notifications.filter((n) => matchesTab(n, activeTab)),
    [notifications, activeTab]
  );

  const tabCounts = useMemo(() => computeTabCounts(notifications), [notifications]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'all', label: 'All Activity' },
    { key: 'payment', label: 'Payment' },
    { key: 'follow_up', label: 'Follow Up' },
    { key: 'delivery', label: 'Delivery' },
  ];

  const handleOpen = (item: NotificationItem) => {
    onHide();
    navigate(item.href, { state: { openTransactionId: item.transactionId } });
  };

  if (!show) return null;

  return (
    <div
      className="absolute right-0 top-full z-[200] mt-2 w-[min(44rem,calc(100vw-1.5rem))] max-h-[min(85vh,720px)]"
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
    >
      <div className="relative flex max-h-[min(85vh,720px)] flex-col overflow-hidden rounded-2xl border border-[#e2e8f0] bg-[#f1f5f9] shadow-[0_24px_64px_rgba(15,23,42,0.18)]">
      

        <button
          type="button"
          onClick={onHide}
          className="absolute right-2 top-2 z-40 rounded-lg p-2 text-slate-500 transition hover:bg-white/80 hover:text-slate-800"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="min-h-0 shrink-0 overflow-hidden rounded-t-2xl bg-gradient-to-br from-sky-200/95 via-sky-50 to-white px-5 pb-4 pt-9 sm:px-7 sm:pb-5 sm:pt-11">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 pr-8">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">Workspace activity</p>
              <h2 className="mb-2 text-lg font-bold tracking-tight text-slate-900 sm:text-xl lg:text-[1.4rem]">
                Stay on top of what needs attention
              </h2>
              <p className="mb-0 text-sm text-slate-600">
                {loading
                  ? 'Loading recent activity…'
                  : notifications.length > 0
                    ? `${notifications.length} recent updates from your portfolio.`
                    : 'You are all caught up for now.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                onHide();
                navigate('/payments/new');
              }}
              className="shrink-0 self-start rounded-full border-2 border-white bg-[#2297D1] px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-[#1b7aad] sm:self-center"
            >
              Create New Item
            </button>
          </div>
        </div>

        <div className="shrink-0 border-t border-[#dce3ea] bg-[#f1f5f9] px-5 py-3 sm:px-7">
          <p className="mb-2 text-sm text-slate-600">Open any item to jump straight to the right page.</p>
          <div className="flex flex-wrap items-center gap-2 gap-y-2">
            {tabs.map((t) => {
              const count = tabCounts[t.key];
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={cn(
                    'rounded-full px-3 py-2 text-sm font-medium transition',
                    isActive
                      ? 'border border-[#2297D1] bg-sky-100/90 text-slate-900 shadow-sm'
                      : 'border border-transparent text-slate-600 hover:bg-white/60'
                  )}
                >
                  {t.label} <span className="tabular-nums text-slate-500">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden bg-[#e8edf2] px-4 py-3 sm:px-6 sm:py-4">
          <div className="theme-scrollbar h-full max-h-[min(50vh,420px)] overscroll-contain pr-1">
            {loading ? (
              <p className="py-8 text-center text-sm text-slate-500">Loading notifications…</p>
            ) : filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500">No items in this view.</p>
            ) : (
              <ul className="space-y-3">
                {filtered.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-xl border border-[#dce3ea] bg-white p-4 shadow-sm transition hover:border-[#c5cdd6] hover:shadow-md"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {CATEGORY_LABEL[item.category]}
                        </p>
                        <p className="mb-1 text-base font-semibold text-slate-900">{item.title}</p>
                        <p className="mb-2 text-sm leading-relaxed text-slate-600">{item.description}</p>
                        <p className="mb-0 text-xs text-slate-500">{item.timestamp}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpen(item)}
                        className="shrink-0 self-start text-sm font-bold uppercase tracking-wide text-[#2297D1] hover:text-[#1b7aad] sm:self-center"
                      >
                        Open
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
